import { type FC, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AxiosError } from 'axios';
import { useInnerWidth } from 'hooks/useInnerWidth';
import i18n from 'i18n';
import { SumsubWidget } from 'pages/PageVerification/components/ui/SumsubWidget';
import { setUserAuthStatusAction } from 'store/auth-reducer/auth.reducer';
import { closePopupAction } from 'store/modals-reducer/modals.reducer';
import {
  clearSumsubDataAction,
  setBtcAddressInputValueAction,
  setNewWalletAddressAction,
  setWalletChangeStatusAction,
} from 'store/profile-reducer/profile.reducer';
import {
  selectBtcInputValue,
  selectSumsubApplicantData,
  selectSumsubFetchStatus,
} from 'store/profile-reducer/profile.selectors';
import {
  requestProfileDataThunkAction,
  requestWalletsDataThunkAction,
} from 'store/profile-reducer/profile.thunk-actions';
import { useAppDispatch } from 'store/store';

import { notifySuccess } from 'utils/notify/notify.utils';

import * as S from './FaceIdCheck.styled';

export const FaceIdCheck: FC = () => {
  const dispatch = useAppDispatch();
  const { innerWidth } = useInnerWidth();

  const btcInputValue = useSelector(selectBtcInputValue);
  const { applicantToken } = useSelector(selectSumsubApplicantData);
  const sumsubDataFetchStatus = useSelector(selectSumsubFetchStatus);
  const interval = useRef<NodeJS.Timer>();

  const hasFetchError = sumsubDataFetchStatus === 'rejected';

  useEffect(() => {
    if (!interval.current) {
      interval.current = setInterval(() => {
        void dispatch(requestWalletsDataThunkAction())
          .unwrap()
          .then((data) => {
            if (btcInputValue && data.btc === btcInputValue) {
              clearInterval(interval.current);
              dispatch(clearSumsubDataAction());
              dispatch(closePopupAction());
              dispatch(setBtcAddressInputValueAction(''));
              dispatch(setWalletChangeStatusAction('initial'));
              dispatch(setNewWalletAddressAction(null));
              notifySuccess(i18n.t('notifyMessages.profile.confirmWalletChangeSuccess'));
            }
          })
          .catch(() => {
            dispatch(closePopupAction());
            void dispatch(requestProfileDataThunkAction())
              .unwrap()
              .catch((err: AxiosError) => {
                if (err.message.includes('403')) {
                  dispatch(setUserAuthStatusAction('unauthorized'));
                }
              });
          });
      }, 2000);
    }

    return () => {
      clearInterval(interval.current);
      dispatch(clearSumsubDataAction());
      dispatch(setBtcAddressInputValueAction(''));
      dispatch(setWalletChangeStatusAction('initial'));
      dispatch(setNewWalletAddressAction(null));
    };
  }, [dispatch, interval, btcInputValue]);

  if (hasFetchError) {
    dispatch(closePopupAction());
    return null;
  }

  if (!btcInputValue) {
    dispatch(closePopupAction());
    return null;
  }

  return (
    <S.FaceIdCheckContainer $widgetWidth={innerWidth}>
      {applicantToken && <SumsubWidget applicantToken={applicantToken} />}
    </S.FaceIdCheckContainer>
  );
};
